/**
 * ProceduralTextures — Canvas-based texture + normal map generators
 *
 * Each returns a cached CanvasTexture (256x256, tiled).
 * Normal maps add surface depth without extra geometry.
 * Auto-assigned per entity type / material theme in ToonMaterials.
 */

import * as THREE from 'three/webgpu';

const SIZE = 256;
const textureCache = new Map();

/**
 * Color maps only — canvas pixels are authored in sRGB, and Texture's default
 * `NoColorSpace` makes the sampler read them as linear, washing every map
 * brighter. Normal maps are non-color data and must NOT get this.
 */
function asColorMap(tex) {
  tex.colorSpace = THREE.SRGBColorSpace;
  // Tiled ground/wall maps are mostly seen at grazing angles; 4 is cheap and
  // universally supported.
  tex.anisotropy = 4;
  return tex;
}

function setTiling(tex) {
  if (tex) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
  }
}

function getCached(key, generator) {
  if (textureCache.has(key)) return textureCache.get(key);
  const result = generator();
  if (result instanceof THREE.Texture) {
    setTiling(result);
  } else {
    setTiling(result.map);
    setTiling(result.normalMap);
  }
  textureCache.set(key, result);
  return result;
}

// ─── Helpers ────────────────────────────────────────────────
function createCanvas() {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  return canvas;
}

function noise2D(x, y) {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

function smoothNoise(x, y, scale) {
  const sx = x / scale, sy = y / scale;
  const ix = Math.floor(sx), iy = Math.floor(sy);
  const fx = sx - ix, fy = sy - iy;
  const a = noise2D(ix, iy), b = noise2D(ix + 1, iy);
  const c = noise2D(ix, iy + 1), d = noise2D(ix + 1, iy + 1);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v;
}

function fbmNoise(x, y, octaves = 4, scale = 64) {
  let val = 0, amp = 0.5, s = scale;
  for (let i = 0; i < octaves; i++) {
    val += smoothNoise(x, y, s) * amp;
    s /= 2; amp /= 2;
  }
  return val;
}

// Encode normal (nx, ny, nz) -> RGB pixel data
function encodeNormal(data, idx, nx, ny, nz) {
  data[idx]     = Math.round((nx * 0.5 + 0.5) * 255);
  data[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
  data[idx + 2] = Math.round((nz * 0.5 + 0.5) * 255);
  data[idx + 3] = 255;
}

// ─── Normal Map Generators ──────────────────────────────────

export function generateNormalMap(type) {
  const key = `normal_${type}`;
  return getCached(key, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');
    const imgData = ctx.createImageData(SIZE, SIZE);
    const d = imgData.data;

    switch (type) {
      case 'stone': {
        for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
          const i = (y * SIZE + x) * 4;
          const n = fbmNoise(x, y, 4, 32);
          const dx = fbmNoise(x + 1, y, 4, 32) - n;
          const dy = fbmNoise(x, y + 1, 4, 32) - n;
          const strength = 3.0;
          encodeNormal(d, i, -dx * strength, -dy * strength, 1);
        }
        break;
      }
      case 'wood': {
        for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
          const i = (y * SIZE + x) * 4;
          const grain = Math.sin(y * 0.3 + smoothNoise(x, y, 16) * 4) * 0.5 + 0.5;
          const dx = Math.cos(y * 0.3 + smoothNoise(x + 1, y, 16) * 4) * 0.1;
          encodeNormal(d, i, dx * 2, -0.05 * grain, 1);
        }
        break;
      }
      case 'metal': {
        for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
          const i = (y * SIZE + x) * 4;
          const gridX = (x % 32 < 2 || x % 32 > 30) ? 1 : 0;
          const gridY = (y % 32 < 2 || y % 32 > 30) ? 1 : 0;
          const edge = Math.max(gridX, gridY);
          const micro = (noise2D(x * 3, y * 3) - 0.5) * 0.15;
          encodeNormal(d, i, edge * 0.3 + micro, edge * 0.3 + micro, 1);
        }
        break;
      }
      case 'crystal': {
        for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
          const i = (y * SIZE + x) * 4;
          // Voronoi-ish facets
          let minD = 999;
          let nearX = 0, nearY = 0;
          for (let cy = -1; cy <= 1; cy++) for (let cx = -1; cx <= 1; cx++) {
            const cellX = Math.floor(x / 64) + cx, cellY = Math.floor(y / 64) + cy;
            const px = (cellX + noise2D(cellX * 7, cellY * 11)) * 64;
            const py = (cellY + noise2D(cellX * 13, cellY * 7)) * 64;
            const dist = Math.sqrt((x - px) ** 2 + (y - py) ** 2);
            if (dist < minD) { minD = dist; nearX = px; nearY = py; }
          }
          const nx = (x - nearX) / (minD + 1) * 0.5;
          const ny = (y - nearY) / (minD + 1) * 0.5;
          encodeNormal(d, i, nx, ny, 1);
        }
        break;
      }
      case 'hex': {
        const r = 32;
        const h = r * Math.sqrt(3) / 2;
        for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
          const i = (y * SIZE + x) * 4;
          // Find nearest hex center
          const row = Math.round(y / (h * 2));
          const xOff = row % 2 ? r * 1.5 : 0;
          const col = Math.round((x - xOff) / (r * 3));
          const cx = col * r * 3 + xOff;
          const cy = row * h * 2;
          const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
          const edge = dist > r * 0.75 ? (dist - r * 0.75) / (r * 0.25) : 0;
          const bevel = Math.min(edge, 1) * 0.6;
          const dx = (x - cx) / (dist + 0.1) * bevel;
          const dy = (y - cy) / (dist + 0.1) * bevel;
          encodeNormal(d, i, dx, dy, 1);
        }
        break;
      }
      default: {
        // Flat normal map
        for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
          encodeNormal(d, (y * SIZE + x) * 4, 0, 0, 1);
        }
      }
    }

    ctx.putImageData(imgData, 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.type = THREE.UnsignedByteType;
    return tex;
  });
}

// ─── Color Texture Generators ───────────────────────────────

export function stoneBrickTexture(color1 = '#4a6a8a', color2 = '#3a5a7a') {
  const key = `stonebrick_${color1}_${color2}`;
  return getCached(key, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');
    const c1 = new THREE.Color(color1);
    const c2 = new THREE.Color(color2);

    ctx.fillStyle = '#333';
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Draw bricks with mortar gaps and color variation
    const brickH = 32, brickW = 64, mortar = 3;
    for (let row = 0; row < SIZE / brickH + 1; row++) {
      const offsetX = row % 2 ? brickW / 2 : 0;
      for (let col = -1; col < SIZE / brickW + 1; col++) {
        const x = col * brickW + offsetX;
        const y = row * brickH;
        const variation = 0.85 + noise2D(col + row * 7, row) * 0.3;
        const bc = (col + row) % 2 === 0 ? c1 : c2;
        const r = Math.round(bc.r * variation * 255);
        const g = Math.round(bc.g * variation * 255);
        const b = Math.round(bc.b * variation * 255);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(x + mortar, y + mortar, brickW - mortar * 2, brickH - mortar * 2);
      }
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.repeat.set(2, 2);
    return asColorMap(tex);
  });
}

export function conveyorTexture(color = '#e67e22', darkColor = '#b35a00') {
  const key = `conveyor_${color}_${darkColor}`;
  return getCached(key, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');

    // Metallic base
    ctx.fillStyle = '#555';
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Chevron stripes
    const stripeW = SIZE / 8;
    for (let i = 0; i < 12; i++) {
      const y = i * stripeW - stripeW;
      ctx.fillStyle = i % 2 === 0 ? color : darkColor;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(SIZE / 2, y + stripeW * 0.5);
      ctx.lineTo(SIZE, y);
      ctx.lineTo(SIZE, y + stripeW);
      ctx.lineTo(SIZE / 2, y + stripeW * 1.5);
      ctx.lineTo(0, y + stripeW);
      ctx.closePath();
      ctx.fill();
    }

    // Wear marks
    ctx.globalAlpha = 0.1;
    for (let i = 0; i < 20; i++) {
      ctx.fillStyle = '#000';
      const wx = Math.random() * SIZE;
      const wy = Math.random() * SIZE;
      ctx.fillRect(wx, wy, Math.random() * 30 + 5, 2);
    }
    ctx.globalAlpha = 1;

    const tex = new THREE.CanvasTexture(canvas);
    tex.repeat.set(1, 1);
    return asColorMap(tex);
  });
}

export function crystalFacetTexture(color = '#e67e22') {
  const key = `crystalfacet_${color}`;
  return getCached(key, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');
    const base = new THREE.Color(color);

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Hex facets with inner gradient glow
    const r = SIZE / 4;
    const h = r * Math.sqrt(3) / 2;
    for (let row = -1; row < 5; row++) {
      for (let col = -1; col < 5; col++) {
        const cx = col * r * 1.5;
        const cy = row * h * 2 + (col % 2 ? h : 0);

        // Inner glow gradient
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 0.8);
        const bright = base.clone().offsetHSL(0, 0.1, 0.15);
        grad.addColorStop(0, '#' + bright.getHexString());
        grad.addColorStop(0.7, color);
        grad.addColorStop(1, '#' + base.clone().multiplyScalar(0.7).getHexString());
        ctx.fillStyle = grad;

        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          const px = cx + r * 0.85 * Math.cos(angle);
          const py = cy + r * 0.85 * Math.sin(angle);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();

        // Hex outline
        ctx.strokeStyle = '#' + base.clone().multiplyScalar(0.5).getHexString() + '80';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }

    return asColorMap(new THREE.CanvasTexture(canvas));
  });
}

export function sparkleTexture(color = '#f1c40f') {
  const key = `sparkle_${color}`;
  return getCached(key, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');
    const base = new THREE.Color(color);

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Starburst pattern
    const spacing = SIZE / 6;
    for (let x = spacing / 2; x < SIZE; x += spacing) {
      for (let y = spacing / 2; y < SIZE; y += spacing) {
        const bright = base.clone().offsetHSL(0, -0.1, 0.3);
        const grad = ctx.createRadialGradient(x, y, 0, x, y, spacing * 0.4);
        grad.addColorStop(0, '#fff');
        grad.addColorStop(0.2, '#' + bright.getHexString());
        grad.addColorStop(1, color);
        ctx.fillStyle = grad;
        ctx.fillRect(x - spacing / 2, y - spacing / 2, spacing, spacing);

        // Cross sparkle
        ctx.strokeStyle = '#ffffff60';
        ctx.lineWidth = 1;
        const arm = spacing * 0.3;
        ctx.beginPath();
        ctx.moveTo(x - arm, y); ctx.lineTo(x + arm, y);
        ctx.moveTo(x, y - arm); ctx.lineTo(x, y + arm);
        ctx.stroke();
      }
    }

    return asColorMap(new THREE.CanvasTexture(canvas));
  });
}

export function mossTexture(color = '#27ae60', variance = 40) {
  const key = `moss_${color}_${variance}`;
  return getCached(key, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');
    const base = new THREE.Color(color);

    // Fill base
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Noise layer with cluster variation
    const imgData = ctx.getImageData(0, 0, SIZE, SIZE);
    const d = imgData.data;
    for (let y = 0; y < SIZE; y++) for (let x = 0; x < SIZE; x++) {
      const i = (y * SIZE + x) * 4;
      const n = fbmNoise(x, y, 3, 48);
      const cluster = fbmNoise(x + 100, y + 100, 2, 96) > 0.5 ? 1.2 : 0.85;
      const v = (n - 0.5) * variance * cluster;
      d[i]     = Math.max(0, Math.min(255, d[i] + v * 0.5));
      d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + v));
      d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + v * 0.3));
    }
    ctx.putImageData(imgData, 0, 0);

    return asColorMap(new THREE.CanvasTexture(canvas));
  });
}

// Legacy aliases — redirect to new texture generators
export function checkerTexture(color1 = '#4a90d9', color2 = '#3a7bc8') {
  return stoneBrickTexture(color1, color2);
}

export function stripeTexture(color = '#e67e22', darkColor = '#d35400') {
  return conveyorTexture(color, darkColor);
}

export function hexTexture(color = '#e67e22') {
  return crystalFacetTexture(color);
}

export function noiseTexture(color = '#95a5a6', variance = 30) {
  return mossTexture(color, variance);
}

export function dotTexture(color = '#f1c40f') {
  return sparkleTexture(color);
}

// ─── Texture Assignment ─────────────────────────────────────

export function getProceduralTexture(entity) {
  const props = entity.properties || {};
  const type = entity.type;

  if (props.isIce) return null;
  if (type === 'obstacle' || type === 'collectible') return null;

  if (props.isConveyor) {
    return conveyorTexture(props.color || '#e67e22');
  }

  if (props.breakable) {
    return crystalFacetTexture(props.color || '#e67e22');
  }

  if (type === 'platform' || type === 'ramp') {
    const c = props.color || '#3498db';
    const base = new THREE.Color(c);
    const dark = base.clone().multiplyScalar(0.85);
    return stoneBrickTexture(c, '#' + dark.getHexString());
  }

  if (type === 'trigger' && props.isGoal) {
    return sparkleTexture(props.color || '#f1c40f');
  }

  if (type === 'decoration') {
    return mossTexture(props.color || '#95a5a6');
  }

  return null;
}

// Get matching normal map type for an entity
export function getNormalMapType(entity, materialTheme) {
  const props = entity.properties || {};
  const type = entity.type;

  if (props.isIce) return 'crystal';
  if (props.breakable) return 'hex';
  if (props.isConveyor) return 'metal';
  if (type === 'collectible') return 'crystal';
  if (type === 'obstacle') return 'metal';

  // Theme-based normal maps
  if (materialTheme) {
    const themeNormals = {
      stone: 'stone', lava_rock: 'stone', wood: 'wood',
      ice_crystal: 'crystal', neon: null, candy: null,
    };
    return themeNormals[materialTheme] ?? 'stone';
  }

  if (type === 'platform' || type === 'ramp') return 'stone';
  if (type === 'decoration') return 'stone';
  return null;
}

/**
 * A trading terminal's screen face.
 *
 * The single most on-brand pixel in the game: every desk, monitor bank and
 * server rack in an arena wears one, so an RWA Royale arena is readable as a
 * trading floor from the match camera without a single word of UI.
 *
 * Deliberately crude — four-bar candles on a grid, drawn chunky enough to
 * survive being 40 pixels tall at 18 units out. A faithful chart would render
 * as grey mush at the only distance anybody sees it from.
 *
 * @param {'up'|'down'} trend - green rally or red selloff
 */
export function tradingScreenTexture(trend = 'up') {
  return getCached(`screen_${trend}`, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');
    const up = trend === 'up';

    ctx.fillStyle = '#07120f';
    ctx.fillRect(0, 0, SIZE, SIZE);

    // Grid first, so the candles sit on top of it like a real terminal.
    ctx.strokeStyle = up ? 'rgba(70,220,160,0.16)' : 'rgba(255,110,110,0.16)';
    ctx.lineWidth = 2;
    for (let i = 1; i < 6; i++) {
      const p = (SIZE / 6) * i;
      ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(SIZE, p); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, SIZE); ctx.stroke();
    }

    // A deterministic walk: two monitors side by side must not show the same
    // chart, and `noise2D` gives a stable one without pulling in Math.random —
    // textures are cached and shared, so a random one would differ per session.
    const bars = 11;
    const w = SIZE / bars;
    let level = SIZE * (up ? 0.72 : 0.28);
    for (let i = 0; i < bars; i++) {
      const step = (noise2D(i * 3.1, up ? 1 : 9) - 0.5) * SIZE * 0.16;
      const drift = up ? -SIZE * 0.035 : SIZE * 0.035;
      const next = Math.max(SIZE * 0.12, Math.min(SIZE * 0.88, level + step + drift));
      const rising = next < level; // canvas y grows downward
      const top = Math.min(level, next);
      const body = Math.max(8, Math.abs(next - level));
      const x = i * w;

      ctx.strokeStyle = rising ? '#2ee08a' : '#ff5b5b';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x + w / 2, top - w * 0.35);
      ctx.lineTo(x + w / 2, top + body + w * 0.35);
      ctx.stroke();

      ctx.fillStyle = rising ? '#2ee08a' : '#ff5b5b';
      ctx.fillRect(x + w * 0.2, top, w * 0.6, body);
      level = next;
    }

    return asColorMap(new THREE.CanvasTexture(canvas));
  });
}

/**
 * A raised access-floor panel — the surface a trading floor is actually built on.
 *
 * This replaces `stoneBrickTexture` on every arena platform, and it is the whole
 * reason the arenas stopped looking like two different games glued together.
 * The props are manufactured objects: flat panels, metal trim, bolts. The
 * platforms they stand on were wearing MASONRY. No amount of colour grading
 * reconciles a stone wall with an office chair — the materials have to agree
 * before the palette gets a vote.
 *
 * Four big panels per tile with inset seams and corner bolts: large features,
 * because at the match camera a tile is roughly a hand's width on screen and
 * anything finer is grain rather than surface.
 */
export function floorPanelTexture(color1 = '#4a6a8a', color2 = '#3a5a7a', repeat = 1) {
  return getCached(`floorpanel_${color1}_${color2}_${repeat}`, () => {
    const canvas = createCanvas();
    const ctx = canvas.getContext('2d');
    const base = new THREE.Color(color1);
    const dark = new THREE.Color(color2);
    const css = (c, k = 1) =>
      `rgb(${Math.round(c.r * 255 * k)},${Math.round(c.g * 255 * k)},${Math.round(c.b * 255 * k)})`;

    // The seam grid shows through the gaps between panels.
    ctx.fillStyle = css(dark, 0.62);
    ctx.fillRect(0, 0, SIZE, SIZE);

    const PANELS = 2;
    const cell = SIZE / PANELS;
    const gap = 5;
    for (let row = 0; row < PANELS; row++) {
      for (let col = 0; col < PANELS; col++) {
        const x = col * cell;
        const y = row * cell;
        // Alternating panels, very slightly apart in value — enough to read as
        // separate plates, not enough to become a checkerboard.
        const shade = (row + col) % 2 === 0 ? 1.0 : 0.93;
        ctx.fillStyle = css(base, shade);
        ctx.fillRect(x + gap, y + gap, cell - gap * 2, cell - gap * 2);

        // Inset border: one lighter edge and one darker, which is what makes a
        // flat rectangle read as a plate with thickness.
        ctx.strokeStyle = css(base, shade * 1.22);
        ctx.lineWidth = 3;
        ctx.strokeRect(x + gap + 7, y + gap + 7, cell - gap * 2 - 14, cell - gap * 2 - 14);
        ctx.strokeStyle = css(dark, 0.8);
        ctx.lineWidth = 2;
        ctx.strokeRect(x + gap, y + gap, cell - gap * 2, cell - gap * 2);

        // Corner bolts — the detail that says "this panel lifts out".
        ctx.fillStyle = css(base, shade * 1.35);
        const inset = gap + 15;
        for (const [bx, by] of [[inset, inset], [cell - inset, inset], [inset, cell - inset], [cell - inset, cell - inset]]) {
          ctx.beginPath();
          ctx.arc(x + bx, y + by, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    const tex = asColorMap(new THREE.CanvasTexture(canvas));
    // Repeat is baked into the cache key rather than mutated after the fact:
    // these textures are shared between every platform of a kind, so setting
    // `.repeat` on one that somebody else is already using retiles their
    // surface too — the kind of coupling that shows up as one arena changing
    // when you load a different one.
    tex.repeat.set(repeat, repeat);
    return tex;
  });
}
